/**
 * @file Reusable table pagination component.
 * @description Prev/next controls, page numbers, and "Showing X–Y of Z" summary.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Plural noun in summary, e.g. "leads", "advertisers" */
  itemLabel?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  /** Hide numbered page buttons when only one page (Prev/Next still hidden) */
  hidePageNumbersWhenSingle?: boolean;
}

type PageItem = number | 'ellipsis';

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  itemLabel = 'items',
  disabled = false,
  ariaLabel = 'Pagination',
  className,
  hidePageNumbersWhenSingle = false,
}) => {
  const safeTotalPages = Math.max(1, totalPages);
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd =
    totalRecords === 0 ? 0 : Math.min(currentPage * pageSize, totalRecords);

  const visiblePageItems = useMemo<PageItem[]>(() => {
    const leadingPagesCount = 3;
    const trailingPagesCount = 2;
    const maxVisiblePages = leadingPagesCount + trailingPagesCount;

    if (safeTotalPages <= maxVisiblePages) {
      return Array.from({ length: safeTotalPages }, (_, i) => i + 1);
    }

    const currentPageInRange = Math.min(Math.max(1, currentPage), safeTotalPages);
    const lastLeadingStart =
      safeTotalPages - trailingPagesCount - leadingPagesCount + 1;
    const leadingStart = Math.min(currentPageInRange, lastLeadingStart);
    const leadingPages = Array.from(
      { length: leadingPagesCount },
      (_, i) => leadingStart + i,
    );
    const trailingStart = safeTotalPages - trailingPagesCount + 1;
    const trailingPages = Array.from(
      { length: trailingPagesCount },
      (_, i) => trailingStart + i,
    );

    return leadingPages[leadingPages.length - 1] + 1 < trailingStart
      ? [...leadingPages, 'ellipsis', ...trailingPages]
      : [...leadingPages, ...trailingPages];
  }, [currentPage, safeTotalPages]);

  const showPageNumbers =
    !hidePageNumbersWhenSingle || safeTotalPages > 1;

  if (totalRecords <= 0) {
    return null;
  }

  const handleChange = (page: number) => {
    if (disabled || page < 1 || page > safeTotalPages || page === currentPage) {
      return;
    }
    onPageChange(page);
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 border-t border-gray-200 bg-gray-50/50 px-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6',
        'md:items-start',
        className,
      )}
    >
      <p className="text-sm text-gray-600">
        Showing{' '}
        <span className="font-semibold text-gray-900">
          {rangeStart}–{rangeEnd}
        </span>{' '}
        of <span className="font-semibold text-gray-900">{totalRecords}</span>{' '}
        {itemLabel}
      </p>

      <nav
        className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end"
        aria-label={ariaLabel}
      >
        <button
          type="button"
          onClick={() => handleChange(currentPage - 1)}
          disabled={disabled || currentPage <= 1}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft size={16} aria-hidden />
          <span className="hidden md:block">Prev</span>
        </button>

        {showPageNumbers && (
          <div className="flex items-center gap-1">
            {visiblePageItems.map((pageItem) =>
              pageItem === 'ellipsis' ? (
                <span
                  key="ellipsis"
                  className="px-2 py-2 text-sm font-medium text-gray-500"
                  aria-hidden="true"
                >
                  ...
                </span>
              ) : (
                <button
                  key={pageItem}
                  type="button"
                  onClick={() => handleChange(pageItem)}
                  disabled={disabled}
                  aria-current={pageItem === currentPage ? 'page' : undefined}
                  className={cn(
                    'min-w-[2rem] rounded-lg px-2 py-2 text-sm font-medium transition-colors',
                    pageItem === currentPage
                      ? 'text-xs md:text-sm bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-sm'
                      : 'text-xs md:text-sm border border-gray-300 bg-white text-gray-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800',
                    disabled && 'pointer-events-none opacity-60',
                  )}
                >
                  {pageItem}
                </button>
              ),
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => handleChange(currentPage + 1)}
          disabled={disabled || currentPage >= safeTotalPages}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-2 text-xs md:text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
         <span className="hidden md:block">Next</span>
          <ChevronRight size={16} aria-hidden />
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
